import axios from "axios";
import {v4 as uuidv4} from "uuid";
import {addFailedFile} from "./uploadState";
import i18n from "i18next";
import {extractTraceIdFromHeaders, generateTraceId, TRACE_ID_HEADER} from "../utils/traceId";

export interface UploadRequestOptions {
  dir?: string;
  fileName?: string;
  randomPath?: boolean;
  folderUploadId?: string;
  folderUploadDate?: string;
  disableCosShortcut?: boolean;
}

export type UploadErrorCallback = (
  errorMessage: string,
  fileName: string,
  traceId?: string,
  fileSize?: number,
  httpStatus?: number
) => void;

export const UPLOAD_SUCCESS_HTTP_STATUS = 554;
const STORAGE_QUOTA_ERROR_CODE = 'upload.storageQuota.exceeded';

export const isSuccessfulUploadStatus = (status: number) => {
  return (status >= 200 && status < 300) || status === UPLOAD_SUCCESS_HTTP_STATUS;
};

const getUploadHttpStatus = (error: any) => {
  if (typeof error?.response?.status === 'number') {
    return error.response.status;
  }
  if (typeof error?.httpStatus === 'number') {
    return error.httpStatus;
  }
  return undefined;
};

const createUploadError = (message: string, httpStatus?: number) => Object.assign(new Error(message), { httpStatus });

export interface TextContentDetail {
  id: string | number;
  content: string;
  fileName: string;
  url: string;
  rawUrl?: string;
  contentType?: string;
  storageType?: string;
  customDomain?: string | null;
}

export interface UploadRecordRemarkResponse {
  id: string | number;
  remark: string | null;
}

let inMemoryUid: string | null = null;

/**
 * Returns a stable browser UID even when storage is blocked or silently fails.
 * Some private/embedded browsers expose localStorage but do not persist writes.
 */
export const getId = (): string => {
  try {
    const stored = localStorage.getItem('fdd_uid')?.trim();
    if (stored) {
      inMemoryUid = stored;
      return stored;
    }
  } catch {
    // Fall back to the in-memory ID below when browser storage is unavailable.
  }

  if (!inMemoryUid) {
    inMemoryUid = uuidv4().replace(/-/g, '');
  }
  try {
    localStorage.setItem('fdd_uid', inMemoryUid);
    const persisted = localStorage.getItem('fdd_uid')?.trim();
    if (persisted) {
      inMemoryUid = persisted;
    }
  } catch {
    // Keep using the generated ID for this page when storage is blocked.
  }
  return inMemoryUid;
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的文件大小字符串
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * 获取随机路径是否开启
 */
export const getRandomPathEnabled = () => {
  const stored = localStorage.getItem('imgcloud_random_path_enabled');
  return stored === null ? true : stored === 'true';
}

const getUploadFileName = (file: any, options: UploadRequestOptions = {}) => {
  return options.fileName || file.webkitRelativePath || file.name;
}

const shouldUseCosShortcut = (file: any, options: UploadRequestOptions = {}) => {
  if (options.disableCosShortcut) {
    return false;
  }

  if (options.randomPath === false) {
    return false;
  }

  return getUploadFileName(file, options) === (file.webkitRelativePath || file.name);
}

/**
 * 上传文件
 * @param file
 * @param onUploadProgress
 * @param errorCallback
 */
export const postFileWithOptions = async (
  file: any,
  onUploadProgress: (p: number) => void,
  errorCallback: UploadErrorCallback,
  options: UploadRequestOptions = {}
) => {
  const traceId = generateTraceId();
  const fileSize = typeof file?.size === 'number' ? file.size : undefined;
  const randomPathEnabled = options.randomPath ?? getRandomPathEnabled();
  let rawLink = '';
  if ((window as any)._debug_ && shouldUseCosShortcut(file, { ...options, randomPath: randomPathEnabled })) {
    rawLink = await postFileToCos(
      file,
      onUploadProgress,
      (errorMessage, fileName, _traceId, failedFileSize, httpStatus) => errorCallback(
        errorMessage,
        fileName,
        traceId,
        failedFileSize ?? fileSize,
        httpStatus
      ),
      options
    ) || '';
  }

  const formData = new FormData();
  if (rawLink) {
    formData.append('fromUrl', rawLink);
  } else {
    formData.append('file', file);
  }
  formData.append('fileName', getUploadFileName(file, options));
  formData.append('uid', getId());
  formData.append('randomPath', randomPathEnabled as any);
  if (options.folderUploadId) {
    formData.append('folderUploadId', options.folderUploadId);
  }
  if (options.folderUploadDate) {
    formData.append('folderUploadDate', options.folderUploadDate);
  }
  if (options.dir) {
    formData.append('dir', options.dir);
  }
  return axios.request({
    method: 'post',
    url: '/img/upload',
    data: formData,
    validateStatus: isSuccessfulUploadStatus,
    headers: {
      [TRACE_ID_HEADER]: traceId,
    },
    onUploadProgress: (p) => {
      const percent = Math.floor(p.loaded / (p.total || 0) * 100);
      console.info('percent is', percent);
      onUploadProgress && onUploadProgress(percent);
    }
  }).then(res => {
    console.log("upload.then");
    const record = res.data;
    const url = record.url;
    const responseTraceId = extractTraceIdFromHeaders(res.headers) || traceId;
    console.info('url is ', url);
    if (res.status !== UPLOAD_SUCCESS_HTTP_STATUS && ((res as any).success === false || record?.success === false)) {
      const errorMessage = (res as any).message || record?.message || i18n.t('error.uploadFailed');
      if (errorCallback != null) {
        errorCallback(errorMessage, file.name, responseTraceId, fileSize, res.status);
        addFailedFile(file.name, errorMessage, file, responseTraceId);
      } else {
        alert(errorMessage);
      }
    }
  }).catch(error => {
    console.warn("upload.catch", error);
    const responseTraceId = extractTraceIdFromHeaders(error.response?.headers) || traceId;
    const httpStatus = getUploadHttpStatus(error);
    if (error.response && error.response.status === 413) {
      const msg = i18n.t('error.fileTooLarge');
      if (errorCallback != null) {
        errorCallback(msg, file.name, responseTraceId, fileSize, httpStatus);
        addFailedFile(file.name, msg, file, responseTraceId);
      } else {
        alert(msg);
      }
    } else {
      const res = error.response?.data;
      const errorMessage = res?.message || error.message || i18n.t('error.uploadFailed');
      if (errorCallback != null) {
        errorCallback(errorMessage, file.name, responseTraceId, fileSize, httpStatus);
        addFailedFile(file.name, errorMessage, file, responseTraceId);
      } else {
        alert(errorMessage);
      }
      if (res?.code === STORAGE_QUOTA_ERROR_CODE) {
        throw createUploadError(errorMessage, httpStatus);
      }
    }
  })
}

export const postFile = async (
  file: any,
  onUploadProgress: (p: number) => void,
  errorCallback: UploadErrorCallback,
  dir?: string,
  options: Omit<UploadRequestOptions, 'dir'> = {}
) => {
  return postFileWithOptions(file, onUploadProgress, errorCallback, { ...options, dir });
}

export const uploadHtmlContent = async (
  fileName: string,
  htmlContent: string,
  onUploadProgress: (p: number) => void,
  errorCallback: (errorMessage: string, fileName: string, fileSize?: number, httpStatus?: number) => void,
) => {
  const file = new File(
    [new Blob([htmlContent], { type: 'text/html;charset=utf-8' })],
    fileName,
    { type: 'text/html;charset=utf-8' }
  );
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', fileName);
  formData.append('uid', getId());
  formData.append('randomPath', getRandomPathEnabled() as any);

  return axios.request({
    method: 'post',
    url: '/img/upload',
    data: formData,
    validateStatus: isSuccessfulUploadStatus,
    onUploadProgress: (p) => {
      const percent = Math.floor(p.loaded / (p.total || 0) * 100);
      onUploadProgress && onUploadProgress(percent);
    }
  }).then(res => {
    const record = res.data;
    if (res.status !== UPLOAD_SUCCESS_HTTP_STATUS && record?.success === false) {
      const errorMessage = record.message || i18n.t('error.uploadFailed');
      throw createUploadError(errorMessage, res.status);
    }
    return record;
  }).catch(error => {
    const httpStatus = getUploadHttpStatus(error);
    const errorMessage = error.response?.data?.message
      || (error.response?.status === 413 ? i18n.t('error.fileTooLarge') : error.message)
      || i18n.t('error.uploadFailed');

    errorCallback(errorMessage, fileName, file.size, httpStatus);
    addFailedFile(fileName, errorMessage, file);
    throw new Error(errorMessage);
  });
}

export const updateHtmlCustomDomain = async (id: string | number, domainPrefix: string, domainSuffix: string) => {
  return axios.post('/img/html/custom-domain', {
    id,
    domainPrefix,
    domainSuffix,
  }).then(res => {
    const result = res.data;
    if (result?.success === false) {
      throw new Error(result.message || i18n.t('htmlCustomDomain.saveFailed'));
    }
    return result?.data || result;
  }).catch(error => {
    const errorMessage = error.response?.data?.message
      || error.message
      || i18n.t('htmlCustomDomain.saveFailed');
    throw new Error(errorMessage);
  });
}

export const getHtmlCustomDomainPolicy = async () => {
  return axios.get('/img/html/custom-domain/policy').then(res => {
    const result = res.data;
    if (result?.success === false) {
      throw new Error(result.message || i18n.t('htmlCustomDomain.policyLoadFailed'));
    }
    return result?.data || result;
  }).catch(error => {
    const errorMessage = error.response?.data?.message
      || error.message
      || i18n.t('htmlCustomDomain.policyLoadFailed');
    throw new Error(errorMessage);
  });
}

export const toggleHtmlShareCollection = async (id: string | number, enabled: boolean) => {
  return axios.post('/img/html-share', {
    uid: getId(),
    id,
    enabled,
  }).then(res => {
    const result = res.data;
    if (result?.success === false) {
      throw new Error(result.message || i18n.t('uploadItem.htmlShareToggleFailed'));
    }
    return result?.data ?? enabled;
  }).catch(error => {
    const errorMessage = error.response?.data?.message
      || error.message
      || i18n.t('uploadItem.htmlShareToggleFailed');
    throw new Error(errorMessage);
  });
}

export const switchUploadRecordDomain = async (id: string | number, domain: string) => {
  return axios.post('/img/switch-domain', {
    id,
    domain,
  }).then(res => {
    const result = res.data;
    if (result?.success === false) {
      throw new Error(result.message || i18n.t('uploadItem.switchDomainSaveFailed'));
    }
    return result?.data || result;
  }).catch(error => {
    const errorMessage = error.response?.data?.message
      || error.message
      || i18n.t('uploadItem.switchDomainSaveFailed');
    throw new Error(errorMessage);
  });
}

export const updateUploadRecordRemark = async (
  id: string | number,
  uid: string,
  remark: string
): Promise<UploadRecordRemarkResponse> => {
  return axios.post('/img/remark', {
    id,
    uid,
    remark,
  }).then(res => {
    const result = res.data;
    if (result?.success === false) {
      throw new Error(result.message || i18n.t('uploadItem.remarkSaveFailed'));
    }
    return result?.data || result;
  }).catch(error => {
    const errorMessage = error.response?.data?.message
      || error.message
      || i18n.t('uploadItem.remarkSaveFailed');
    throw new Error(errorMessage);
  });
}

export const reuploadFile = async (
  id: string | number,
  file: File,
  onUploadProgress: (p: number) => void,
  errorCallback: (errorMessage: string, fileName: string, fileSize?: number, httpStatus?: number) => void,
) => {
  const formData = new FormData();
  formData.append('id', String(id));
  formData.append('file', file);

  return axios.request({
    method: 'post',
    url: '/img/reupload',
    data: formData,
    validateStatus: isSuccessfulUploadStatus,
    onUploadProgress: (p) => {
      const percent = Math.floor(p.loaded / (p.total || 0) * 100);
      onUploadProgress && onUploadProgress(percent);
    }
  }).then(res => {
    const result = res.data;
    if (res.status !== UPLOAD_SUCCESS_HTTP_STATUS && result?.success === false) {
      const errorMessage = result.message || i18n.t('error.uploadFailed');
      errorCallback(errorMessage, file.name, file.size, res.status);
      addFailedFile(file.name, errorMessage, file);
      return;
    }
    return result?.data;
  }).catch(error => {
    const httpStatus = getUploadHttpStatus(error);
    const errorMessage = error.response?.data?.message
      || (error.response?.status === 413 ? i18n.t('error.fileTooLarge') : error.message)
      || i18n.t('error.uploadFailed');

    errorCallback(errorMessage, file.name, file.size, httpStatus);
    addFailedFile(file.name, errorMessage, file);
  });
}

export const getTextContentDetail = async (id: string | number): Promise<TextContentDetail> => {
  try {
    const response = await axios.get('/img/text-content', {
      params: {
        id,
        _t: Date.now(),
      },
    });
    const result = response.data;
    if (result?.success === false) {
      throw new Error(result.message || i18n.t('error.uploadFailed'));
    }
    if (typeof result?.data === 'string') {
      return {
        id,
        content: result.data,
        fileName: '',
        url: '',
      };
    }
    return {
      id: result?.data?.id ?? id,
      content: result?.data?.content ?? '',
      fileName: result?.data?.fileName ?? '',
      url: result?.data?.url ?? '',
      rawUrl: result?.data?.rawUrl,
      contentType: result?.data?.contentType,
      storageType: result?.data?.storageType,
      customDomain: result?.data?.customDomain,
    };
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message || i18n.t('error.uploadFailed'));
  }
};

export const getTextContent = async (id: string | number): Promise<string> => {
  const detail = await getTextContentDetail(id);
  return detail.content;
};

export const saveTextContent = async (id: string | number, content: string) => {
  try {
    const response = await axios.request({
      method: 'post',
      url: '/img/text-content',
      data: {
        id,
        content,
      },
    });
    const result = response.data;
    if (result?.success === false) {
      throw new Error(result.message || i18n.t('error.uploadFailed'));
    }
    return result;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message || i18n.t('error.uploadFailed'));
  }
};

/**
 * 上传文件并返回持久化记录
 * @param file 文件
 * @param dir 目录
 * @returns 上传后的记录
 */
export interface UploadedFileRecord {
  id: number;
  url: string;
  fileName: string;
  contentType: string;
}

export const uploadFileAndGetRecord = async (
  file: File,
  dir?: string
): Promise<UploadedFileRecord> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', file.webkitRelativePath || file.name);
  formData.append('uid', getId());
  formData.append('randomPath', getRandomPathEnabled() as any);
  if (dir) {
    formData.append('dir', dir);
  }

  try {
    const response = await axios.request({
      method: 'post',
      url: '/img/upload',
      data: formData,
      validateStatus: isSuccessfulUploadStatus,
    });

    const record = response.data;

    if (record.id && record.url) {
      return {
        id: Number(record.id),
        url: String(record.url),
        fileName: String(record.fileName || file.name),
        contentType: String(record.contentType || file.type),
      };
    }
    throw new Error(record.message || i18n.t('error.uploadFailed'));
  } catch (error: any) {
    // 如果是 axios 错误，尝试从响应中提取服务端错误信息
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    // 如果是文件过大的错误
    if (error.response?.status === 413) {
      throw new Error(i18n.t('error.fileTooLarge'));
    }
    // 如果已经是 Error 实例（包括上面抛出的业务错误），直接重新抛出
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('error.uploadFailed'));
  }
};

export const uploadFileAndGetUrl = async (file: File, dir?: string): Promise<string> => {
  const record = await uploadFileAndGetRecord(file, dir);
  return record.url;
};

const getLocalDateSegment = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}${month}${day}`;
}

export const postFileToCos = async (
  file: any,
  onUploadProgress: (p: number) => void,
  errorCallback: UploadErrorCallback,
  options: UploadRequestOptions = {}
) => {
  if (!(window as any)._info?.contextPath) {
    alert(i18n.t('error.contactOnError'))
    return;
  }

  let key = '';
  const uploadFileName = getUploadFileName(file, options);
  if (options.folderUploadId) {
    key = `autoupload/${(window as any)._info?.contextPath}/${options.folderUploadDate || getLocalDateSegment()}/${options.folderUploadId}/${uploadFileName}`;
  } else {
    key = `autoupload/${(window as any)._info?.contextPath}/${(window as any)._dir || "default"}/` + uploadFileName;
  }
  
  // 将空格替换成下划线
  if (!options.folderUploadId) {
    key = key.replace(/ /g, '_');
  }

  const credentialResponse = await fetch(
    `/img/get_temp_ak_sk?fileSize=${encodeURIComponent(String(file?.size || 0))}`
  );
  const credentialData = await credentialResponse.json();
  if (
    !credentialResponse.ok ||
    credentialData?.success === false ||
    credentialData?.isSuccess === false ||
    !credentialData?.SecretId ||
    !credentialData?.SecretKey
  ) {
    const errorMessage = credentialData?.message || i18n.t('error.uploadFailed');
    errorCallback(errorMessage, file.name, undefined, file?.size, credentialResponse.status);
    throw createUploadError(errorMessage, credentialResponse.status);
  }

  // The COS SDK is only needed for this direct-upload path, so keep it out of the main bundle.
  const { default: COS } = await import(/* webpackChunkName: "cos-sdk" */ 'cos-js-sdk-v5');
  const cos = new COS({
    SecretId: credentialData.SecretId,
    SecretKey: credentialData.SecretKey
  });
  await new Promise<void>((resolve, reject) => {
    cos.uploadFile({
      Bucket: "7295f60f8080-1257812490",
      Region: "ap-beijing",
      Key: key,
      Body: file,
      onProgress: function(progressData) {
        console.log('上传进度：', progressData);
        onUploadProgress(progressData.percent * 100);
      }
    }, function (err, data) {
      console.log('上传结束', err || data);
      if (err) {
        errorCallback(err.message, file.name, undefined, file?.size);
        reject(createUploadError(err.message));
        return;
      }
      resolve();
    });
  });

  // 对key进行URL编码，处理特殊字符（空格已替换为下划线）
  // 注意：只编码路径部分，保留斜杠不编码
  const encodedKey = key.split('/').map(segment => encodeURIComponent(segment)).join('/');
  return `https://7295f60f8080-1257812490.cos.ap-beijing.tencentcos.cn/${encodedKey}`;
}
